import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  MenuItem,
  Alert,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaymentIcon from '@mui/icons-material/Payment';
import axiosInstance from '../../store/api';
import UserAccessModal from './UserAccessModal';
import PendingPaymentsTable from './PendingPaymentsTable';
import AdminAnalytics from './AdminAnalytics';
import AdminReports from './AdminReports';
import UsageAnalytics, { formatUsageDate } from './UsageAnalytics';
import { APPS } from '../../utils/cloudStorage';
import { getPhotosUploadStatus } from './retailPhotosAccess';
import { fmtStorageBytes } from './RetailPhotosStorageBar';
import BlogAdmin from '../blog/BlogAdmin';
import ArticleIcon from '@mui/icons-material/Article';
import { useAuthStore } from '../../store/authStore';

function getAccessSummary(user, catalog) {
  const access = user.appsAccess || {};
  let active = 0;
  let expiring = 0;
  let premium = 0;
  let closestExpiry = null;

  Object.entries(catalog).forEach(([key, app]) => {
    const a = access[key];
    if (!a?.enabled) return;
    const diff = a.expiresAt ? Math.ceil((new Date(a.expiresAt) - new Date()) / 86400000) : Infinity;
    if (diff > 0) {
      active++;
      if (app.premium) premium++;
      if (diff <= 7) expiring++;
      if (Number.isFinite(diff) && (closestExpiry === null || diff < closestExpiry)) closestExpiry = diff;
    }
  });

  return { active, expiring, premium, closestExpiry };
}

export default function AppsAccessDashboard() {
  const userRole = useAuthStore(s => s.user?.role);
  const [users, setUsers] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [storageByUserId, setStorageByUserId] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [tab, setTab] = useState(0);
  const [blogDirty, setBlogDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [loading, setLoading] = useState(false);
  const [loadErrors, setLoadErrors] = useState([]);
  const [usageData, setUsageData] = useState(null);
  const [usageDays, setUsageDays] = useState('30');
  const [usageApp, setUsageApp] = useState('');
  const [usageReload, setUsageReload] = useState(0);
  const usageByUser = useMemo(() => Object.fromEntries((usageData?.users || []).map(row => [row.userId, row])), [usageData]);

  const load = async () => {
    setLoading(true);
    setLoadErrors([]);
    setUsageReload(value => value + 1);
    try {
      const sources = [
        ['/users', 'usuarios', data => setUsers(data)],
        ['/subscriptions/pending', 'pagos', data => setPendingPayments(data)],
        ['/users/retail-storage-usage', 'almacenamiento', data => setStorageByUserId(data?.usageByUserId || {})],
      ];
      await Promise.allSettled(sources.map(async ([url, label, save]) => {
        try { const response = await axiosInstance.get(url); save(response.data); }
        catch { setLoadErrors(previous => [...previous, `No se pudo actualizar ${label}. Los datos anteriores, si existen, se conservan.`]); }
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === 'admin') load();
  }, [userRole]);

  const handleSaved = (updatedUser) => {
    setUsers(prev => prev.map(u => (u._id === updatedUser._id ? updatedUser : u)));
    setSelectedUser(null);
    axiosInstance.get('/users/retail-storage-usage').then((res) => {
      setStorageByUserId(res.data?.usageByUserId || {});
    }).catch(() => {});
  };

  const filteredUsers = useMemo(() => {
    let result = users;
    if (usageApp && usageData) result = result.filter(user => usageByUser[user._id]);

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(u =>
        `${u.nombre} ${u.apellido} ${u.email} ${u.personal_code}`.toLowerCase().includes(q)
      );
    }

    // Filter by status
    if (filterStatus !== 'todos') {
      result = result.filter(u => {
        const summary = getAccessSummary(u, APPS);
        if (filterStatus === 'premium') return summary.premium > 0;
        if (filterStatus === 'activos') return summary.active > 0;
        if (filterStatus === 'expiring') return summary.expiring > 0;
        if (filterStatus === 'inactivos') return summary.active === 0;
        return true;
      });
    }

    return result;
  }, [users, search, filterStatus, usageApp, usageData, usageByUser]);

  const getUserPaymentStatus = (userId) => {
    const userPayments = pendingPayments.filter(p => p.user?._id === userId);
    if (userPayments.length === 0) return null;
    if (userPayments.some(p => p.status === 'PENDING_REVIEW')) {
      return <Chip label="Pago pendiente" color="warning" size="small" />;
    }
    return null;
  };

  if (userRole !== 'admin') return <Typography>No tienes acceso al panel de administración.</Typography>;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={800}>Panel de Control</Typography>
        <Tooltip title="Recargar datos">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {loadErrors.map(message => <Alert key={message} severity="error" sx={{ mb: 1 }}>{message}</Alert>)}

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => { if (tab === 4 && blogDirty && !window.confirm('Tienes cambios del blog sin guardar. ¿Quieres descartarlos?')) return; setBlogDirty(false); setTab(v); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<Badge badgeContent={pendingPayments.length} color="error"><PaymentIcon /></Badge>} label="Pagos" iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<PeopleAltIcon />} label={`Usuarios (${users.length})`} iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<BarChartIcon />} label="Uso real" iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<AssessmentIcon />} label="Accesos" iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
        <Tab icon={<ArticleIcon />} label="Blog" iconPosition="start" sx={{ textTransform: 'none', fontWeight: 600 }} />
      </Tabs>
      {tab === 4 && <BlogAdmin onDirtyChange={setBlogDirty} />}

      {/* Tab 0: Pagos Pendientes */}
      {tab === 0 && (
        <PendingPaymentsTable payments={pendingPayments} onResolved={load} />
      )}

      {/* Tab 1: Usuarios */}
      {tab === 1 && (
        <Paper sx={{ p: 3 }}>
          {/* Filtros */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <TextField
              size="small"
              placeholder="Buscar por nombre, email o código..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 200 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20 }} /></InputAdornment>,
              }}
            />
            <TextField
              select
              size="small"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="premium">Con Premium</MenuItem>
              <MenuItem value="activos">Con accesos habilitados</MenuItem>
              <MenuItem value="expiring">Por vencer</MenuItem>
              <MenuItem value="inactivos">Sin accesos habilitados</MenuItem>
            </TextField>
            <TextField select label="Período de uso" size="small" value={usageDays} onChange={e => setUsageDays(e.target.value)} sx={{ minWidth: 170 }}><MenuItem value="7">Últimos 7 días</MenuItem><MenuItem value="30">Últimos 30 días</MenuItem></TextField>
            <TextField select label="Herramienta utilizada" size="small" value={usageApp} onChange={e => setUsageApp(e.target.value)} sx={{ minWidth: 200 }}><MenuItem value="">Todas</MenuItem>{Object.entries(APPS).filter(([, tool]) => !tool.hidden).map(([key, tool]) => <MenuItem key={key} value={key}>{tool.title}</MenuItem>)}</TextField>
          </Stack>
          <Alert severity={usageData ? 'info' : 'warning'} sx={{ mb: 2 }}>{usageData ? `Actividad registrada en los últimos ${usageDays} días. Medición desde ${formatUsageDate(usageData.startedAt)}. Sin historial anterior.` : 'Uso real no disponible. Consulta la pestaña Uso real para ver el estado de la medición.'}</Alert>

          <Typography variant="caption" color="text.secondary" mb={1} display="block">
            {filteredUsers.length} de {users.length} usuarios
          </Typography>

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Herramientas utilizadas</TableCell>
                  <TableCell>Último uso</TableCell>
                  <TableCell>Acciones completadas</TableCell>
                  <TableCell align="center">Accesos habilitados</TableCell>
                  <TableCell align="center">Premium</TableCell>
                  <TableCell align="center">Fotos POS / Uso S3</TableCell>
                  <TableCell align="center">Vence</TableCell>
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map(user => {
                  const summary = getAccessSummary(user, APPS);
                  const retailAccess = user.appsAccess?.retail_pos_v1 || {};
                  return (
                    <TableRow key={user._id} sx={summary.expiring > 0 ? { bgcolor: '#fff8e1' } : undefined}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{user.nombre} {user.apellido}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{user.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.personal_code || '—'} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                      </TableCell>
                      <TableCell align="center">
                        {usageData ? (usageByUser[user._id]?.apps.map(key => APPS[key]?.title || key).join(', ') || 'Sin actividad registrada en este período') : 'Uso no disponible'}
                      </TableCell>
                      <TableCell>{usageData ? formatUsageDate(usageByUser[user._id]?.lastUse) : 'Uso no disponible'}</TableCell>
                      <TableCell>{usageData ? usageByUser[user._id]?.completed || 0 : '—'}</TableCell>
                      <TableCell align="center">
                        <Chip label={summary.active} size="small" color={summary.active > 0 ? 'success' : 'default'} sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="center">
                        {summary.premium > 0
                          ? <Chip label={summary.premium} size="small" color="warning" sx={{ fontWeight: 700 }} />
                          : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {retailAccess.imagenesUpload ? (() => {
                          const ps = getPhotosUploadStatus(retailAccess);
                          const exp = retailAccess.imagenesUploadExpiresAt?.substring(0, 10);
                          const usage = storageByUserId[user._id] || { totalBytes: 0, fileCount: 0 };
                          const limitMb = retailAccess.storageLimitMb || 500;
                          const used = usage.totalBytes || 0;
                          const pct = Math.min(100, (used / (limitMb * 1024 * 1024)) * 100);
                          return (
                            <Tooltip
                              title={`${fmtStorageBytes(used)} de ${limitMb} MB · ${usage.fileCount || 0} imágenes${exp ? ` · Vence ${exp}` : ''}`}
                            >
                              <Stack spacing={0.25} alignItems="center">
                                <Typography variant="caption" fontWeight={700} sx={{ fontSize: 10 }}>
                                  {fmtStorageBytes(used)} / {limitMb} MB
                                </Typography>
                                <Chip label={ps.label} size="small" color={ps.color} sx={{ height: 18, fontSize: 9 }} />
                                {used > 0 && (
                                  <Box sx={{ width: 72, height: 4, bgcolor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box
                                      sx={{
                                        width: `${pct}%`,
                                        height: '100%',
                                        bgcolor: pct > 85 ? 'error.main' : pct > 60 ? 'warning.main' : 'success.main',
                                      }}
                                    />
                                  </Box>
                                )}
                              </Stack>
                            </Tooltip>
                          );
                        })() : (
                          (storageByUserId[user._id]?.totalBytes > 0)
                            ? (
                              <Tooltip title="Tiene archivos en S3 pero fotos premium inactivas">
                                <Typography variant="caption" color="text.secondary">
                                  {fmtStorageBytes(storageByUserId[user._id].totalBytes)}
                                </Typography>
                              </Tooltip>
                            )
                            : '—'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {summary.closestExpiry !== null
                          ? <Chip
                              label={`${summary.closestExpiry}d`}
                              size="small"
                              color={summary.closestExpiry <= 3 ? 'error' : summary.closestExpiry <= 7 ? 'warning' : 'success'}
                              sx={{ fontWeight: 700 }}
                            />
                          : '—'}
                      </TableCell>
                      <TableCell align="center">
                        {getUserPaymentStatus(user._id)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setSelectedUser(user)}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Gestionar
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {selectedUser && (
            <UserAccessModal
              open
              user={selectedUser}
              catalog={APPS}
              onClose={() => setSelectedUser(null)}
              onSaved={handleSaved}
            />
          )}
        </Paper>
      )}

      {/* Tab 2: Analytics */}
      <Box sx={{ display: tab === 2 ? 'block' : 'none' }}>
        <UsageAnalytics users={users} catalog={APPS} days={usageDays} app={usageApp} onDaysChange={setUsageDays} onAppChange={setUsageApp} reload={usageReload} onLoaded={setUsageData} />
      </Box>

      {/* Tab 3: Reportes */}
      {tab === 3 && (
        <><AdminAnalytics users={users} catalog={APPS} pendingPayments={pendingPayments} /><AdminReports users={users} catalog={APPS} /></>
      )}
    </Box>
  );
}
